import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { ActionModule } from '@typesApp/ActionModule'
import { AutoNullifyStatus } from '@typesApp/ClaimRecord'
import { AuthToken, Customer, CustomerContext } from '@typesApp/Customer'
import { HostEnvironment, InitialHostEnvironment } from '@typesApp/Environment'
import { authApi } from '@redux/queries/auth'
import { AppState } from '@redux/store'
import { clearLoginRedirectParam } from '@utils/navigationUtil'

type ConnectorState = {
  environment: HostEnvironment
  customerContext?: CustomerContext
  customer?: Customer
  sessionExpired?: boolean
  authTokenReady?: boolean
  authTokenError?: boolean
  authToken?: AuthToken
  isSessionInitializing?: boolean
  isClaimRecordUpdateInProgress?: boolean
  isClaimRecordUpdatePollingInProgress?: boolean
  isLoggingOut?: boolean
  isOrderComplete?: boolean
  actionModule?: ActionModule
  lastFormActionTime?: Date
  autoNullify?: AutoNullifyStatus
}

const initialState: ConnectorState = {
  environment: InitialHostEnvironment,
}

export const connectorSlice = createSlice({
  name: 'connector',
  initialState: initialState,
  reducers: {
    connectorAfterCreateClaimRecord(state) {
      state.isOrderComplete = false
    },
    connectorSetSessionInitializing(state, action: PayloadAction<boolean>) {
      state.isSessionInitializing = action.payload
    },
    setClaimRecordUpdateInProgress(state, action: PayloadAction<boolean>) {
      state.isClaimRecordUpdateInProgress = action.payload
    },
    setClaimRecordUpdatePollingInProgress(state, action: PayloadAction<boolean>) {
      state.isClaimRecordUpdatePollingInProgress = action.payload
    },
    connectorInitCustomer(state, action: PayloadAction<Customer>) {
      state.customer = action.payload

      // propagate userId to customerContext if applicable
      if (state.customer?.id && state.customerContext && !state.customerContext.userId) {
        state.customerContext.userId = state.customer.id
      }
    },
    connectorInitCustomerContext(state, action: PayloadAction<CustomerContext>) {
      // logging out, still same customer
      if (state.isLoggingOut && action.payload.customerType !== 'GUEST') {
        return
      }

      const { userId, ...rest } = action.payload
      state.customerContext = { ...state.customerContext, ...rest }

      // NOTE: update userId only if available
      // LCR is still on an older integration and does not send the userId
      if (userId) {
        state.customerContext.userId = userId
        state.customer = {
          ...state.customer,
          id: userId,
        }
      }

      const isLoggedInOrGuest = action.payload.customerType === 'GUEST' || action.payload.customerType === 'REGISTERED'
      if (isLoggedInOrGuest) {
        clearLoginRedirectParam()
      }
    },
    connectorPrepareGuestLogin(state) {
      // isLoggingOut flag remains true after authenticated user logs out
      // need to reset prior to guest login
      state.isLoggingOut = false
      state.sessionExpired = false
    },
    connectorInitEnvironment(state, action: PayloadAction<HostEnvironment>) {
      state.environment = action.payload
    },
    connectorInitActionModule(state, action: PayloadAction<ActionModule>) {
      state.actionModule = action.payload
    },
    connectorLogoutInProgress(state) {
      return {
        ...initialState,
        isLoggingOut: true,
      }
    },
    connectorOrderComplete(state) {
      state.isOrderComplete = true
    },
    connectorLastFormActionTime(state) {
      state.lastFormActionTime = new Date()
    },
    sessionExpired(state) {
      state.sessionExpired = true
      state.authTokenReady = false
      state.authToken = undefined
      state.isSessionInitializing = false

      // reset current customer
      state.customer = undefined
    },
    resetSessionExpired(state) {
      state.sessionExpired = false
    },
    authTokenError(state) {
      state.authTokenError = true
    },
    connectorAutoNullifyStatus(state, action: PayloadAction<AutoNullifyStatus>) {
      state.autoNullify = {
        ...state.autoNullify,
        ...action.payload,
      }
    },
    connectorOnLogin(state) {
      state.isLoggingOut = undefined
      state.sessionExpired = undefined
    },
    resetConnectorInitialState(state) {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * NOTE: need to set authToken in redux state immediately on successful fetch
     * so that API calls have immediate access to the token.
     */
    builder.addMatcher(authApi.endpoints.getAuthToken.matchFulfilled, (state, action) => {
      state.authToken = action.payload
      state.authTokenReady = true
    })
  },
})

export const {
  connectorAfterCreateClaimRecord,
  connectorSetSessionInitializing,
  setClaimRecordUpdateInProgress,
  setClaimRecordUpdatePollingInProgress,
  connectorInitCustomer,
  connectorInitCustomerContext,
  connectorPrepareGuestLogin,
  connectorInitEnvironment,
  connectorInitActionModule,
  connectorLogoutInProgress,
  connectorLastFormActionTime,
  connectorOrderComplete,
  sessionExpired,
  resetSessionExpired,
  authTokenError,
  connectorOnLogin,
  connectorAutoNullifyStatus,
  resetConnectorInitialState,
} = connectorSlice.actions

export const connectorOrderCompleteSelector = (state: AppState): boolean | undefined => state.connector.isOrderComplete
export const connectorCustomerContextSelector = (state: AppState): CustomerContext | undefined =>
  state.connector.customerContext
export const connectorCustomerSelector = (state: AppState): Customer | undefined => state.connector.customer
export const connectorEnvironmentSelector = (state: AppState): HostEnvironment => state.connector.environment
export const connectorActionModuleSelector = (state: AppState): ActionModule | undefined => state.connector.actionModule
export const connectorLastFormActionTimeSelector = (state: AppState): Date | undefined =>
  state.connector.lastFormActionTime
export const isSessionInitializingSelector = (state: AppState): boolean | undefined =>
  state.connector.isSessionInitializing
export const claimRecordUpdateInProgressSelector = (state: AppState): boolean | undefined =>
  state.connector.isClaimRecordUpdateInProgress
export const claimRecordUpdatePollingInProgressSelector = (state: AppState): boolean | undefined =>
  state.connector.isClaimRecordUpdatePollingInProgress
export const sessionExpiredSelector = (state: AppState): boolean | undefined => state.connector.sessionExpired
export const authTokenReadySelector = (state: AppState): boolean | undefined => state.connector.authTokenReady
export const authTokenSelector = (state: AppState): AuthToken | undefined => state.connector.authToken
export const authTokenErrorSelector = (state: AppState): boolean | undefined => state.connector.authTokenError
export const loggingOutSelector = (state: AppState): boolean | undefined => state.connector.isLoggingOut
export const connectorAutoNullifyStatusSelector = (state: AppState): AutoNullifyStatus | undefined =>
  state.connector.autoNullify
